import { createRoute, Outlet, type ParsedLocation, redirect } from "@tanstack/react-router";
import BidPage from "pages/app/BidPage";
import { BidResponsesListPage } from "pages/app/BidResponsesPage";
import { BidsPage } from "pages/app/BidsPage";

import BidSelfServe from "components/account_management/qualification-criteria/BidSelfServe";
import { BidResponsePage } from "components/bid_module/bid-response/BidResponsePage";
import { BidResponsesProcessingProvider } from "components/bid_module/bid-response/BidResponsesProcessingProvider";
import BidReportLoadingPage from "components/bid_module/common_report_components/BidReportLoadingPage";
import { PublicQualificationReport } from "components/bid_module/qualification_report_section/PublicQualificationReport";
import QualificationReport from "components/bid_module/qualification_report_section/QualificationReport";
import { BidNoBidReport } from "components/bid_module/requirement_summary_report_section/BidNoBidReport";
import { SharedBidReportView } from "components/bid_module/requirement_summary_report_section/SharedBidReportView";
import BidBuyerActivityPage from "components/bids/bid_content/BidBuyerActivityPage";
import BidDocumentsList from "components/bids/bid_documents/BidDocumentsList";
import { routeSegments } from "lib/routes";
import { rootRoute } from "../rootRoute";

function BidsLayout() {
  return (
    <BidResponsesProcessingProvider>
      <Outlet />
    </BidResponsesProcessingProvider>
  );
}

export const bidsLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/bids",
  staticData: { pageName: "", breadcrumbLabel: "Bids" },
  component: BidsLayout,
});

export const bidsIndexRoute = createRoute({
  getParentRoute: () => bidsLayoutRoute,
  path: "/",
  staticData: { pageName: "Bids" },
  component: BidsPage,
});

// Layout route for /bids/$id — nests all bid sub-pages so useMatches()
// returns this in the match chain, enabling auto-breadcrumbs.
function BidIdLayout() {
  return <Outlet />;
}

export const bidIdLayoutRoute = createRoute({
  getParentRoute: () => bidsLayoutRoute,
  path: "/$id",
  staticData: { pageName: "" },
  component: BidIdLayout,
});

export const bidDetailRoute = createRoute({
  getParentRoute: () => bidIdLayoutRoute,
  path: "/",
  staticData: { pageName: "Bid" },
  component: function BidDetailRouteComp() {
    const { id } = bidDetailRoute.useParams();
    return <BidPage id={id} />;
  },
});

export const bidSelfServeRoute = createRoute({
  getParentRoute: () => bidIdLayoutRoute,
  path: `/${routeSegments.requirementSummary}/$bidReportId/${routeSegments.bidSelfServe}`,
  staticData: { pageName: "Bid self-serve" },
  component: BidSelfServe,
});

export const bidQualificationRoute = createRoute({
  getParentRoute: () => bidIdLayoutRoute,
  path: `/${routeSegments.requirementSummary}/$bidReportId`,
  staticData: { pageName: "Bid qualification" },
  component: function BidQualificationRoute() {
    const { id, bidReportId } = bidQualificationRoute.useParams();
    return <BidNoBidReport bidReportId={bidReportId} bidId={id} />;
  },
});

// Legacy redirect routes — preserve old bid_qualification URLs for bookmarked links
export const bidQualificationLegacyRedirectRoute = createRoute({
  getParentRoute: () => bidIdLayoutRoute,
  path: `/${routeSegments.bidQualification}/$bidReportId`,
  staticData: { pageName: "" },
  beforeLoad: ({
    params,
    search,
    location,
  }: {
    params: { id: string; bidReportId: string };
    search: Record<string, unknown>;
    location: ParsedLocation;
  }) => {
    /**
     * Throwing is tanstack router convention for redirects in beforeLoad functions. This
     * completely interrupts component mounting and forces a full redirect as the router
     * catches Redirect errors. We don't want any components or hooks to run on the legacy
     * route as that will result in duplicate API calls
     */
    throw redirect({
      to: `/bids/$id/${routeSegments.requirementSummary}/$bidReportId`,
      params: { id: params.id, bidReportId: params.bidReportId },
      search,
      hash: location.hash,
      replace: true,
    });
  },
});

export const bidSelfServeLegacyRedirectRoute = createRoute({
  getParentRoute: () => bidIdLayoutRoute,
  path: `/${routeSegments.bidQualification}/$bidReportId/${routeSegments.bidSelfServe}`,
  staticData: { pageName: "" },
  beforeLoad: ({
    params,
    search,
    location,
  }: {
    params: { id: string; bidReportId: string };
    search: Record<string, unknown>;
    location: ParsedLocation;
  }) => {
    throw redirect({
      to: `/bids/$id/${routeSegments.requirementSummary}/$bidReportId/${routeSegments.bidSelfServe}`,
      params: { id: params.id, bidReportId: params.bidReportId },
      search,
      hash: location.hash,
      replace: true,
    });
  },
});

export const bidResponsesLayoutRoute_nested = createRoute({
  getParentRoute: () => bidIdLayoutRoute,
  path: "/bid_responses",
  staticData: { pageName: "", breadcrumbLabel: "Bid responses" },
  component: Outlet,
});

export const bidResponsesIndexRoute_nested = createRoute({
  getParentRoute: () => bidResponsesLayoutRoute_nested,
  path: "/",
  staticData: { pageName: "Bid responses" },
  component: function BidResponsesRouteComp() {
    const { id } = bidResponsesIndexRoute_nested.useParams();
    return <BidResponsesListPage bidId={id} />;
  },
});

export const bidResponseRoute = createRoute({
  getParentRoute: () => bidResponsesLayoutRoute_nested,
  path: "/$bidResponseId",
  staticData: { pageName: "Bid response" },
  component: function BidResponseRouteComp() {
    const { bidResponseId } = bidResponseRoute.useParams();
    return <BidResponsePage bidResponseId={bidResponseId} />;
  },
});

export const bidDocumentsRoute = createRoute({
  getParentRoute: () => bidIdLayoutRoute,
  path: "/tender-documents",
  staticData: { pageName: "Bid documents", breadcrumbLabel: "Tender documents" },
  component: function BidDocumentsRouteComp() {
    const { id } = bidDocumentsRoute.useParams();
    return <BidDocumentsList id={id} />;
  },
});

export const bidBuyerActivityRoute = createRoute({
  getParentRoute: () => bidIdLayoutRoute,
  path: "/buyer-activity",
  staticData: { pageName: "Bid buyer activity", breadcrumbLabel: "Buyer activity" },
  component: function BidBuyerActivityRouteComp() {
    const { id } = bidBuyerActivityRoute.useParams();
    return <BidBuyerActivityPage bidId={id} />;
  },
});

export const qualificationReportRoute = createRoute({
  getParentRoute: () => bidIdLayoutRoute,
  path: "/qualification-report/$qualificationReportId",
  staticData: { pageName: "Qualification report", breadcrumbLabel: "Qualification report" },
  component: function QualificationReportRouteComp() {
    const { id, qualificationReportId } = qualificationReportRoute.useParams();
    return <QualificationReport qualificationReportId={qualificationReportId} bidId={id} />;
  },
});

// Loading "purgatory" routes — index of each report section. Visiting
// /bids/$id/requirement-summary (no $bidReportId) means we're waiting for
// the backend to publish a requirement-summary report id; same for the
// qualification-report variant.
export const requirementSummaryLoadingRoute = createRoute({
  getParentRoute: () => bidIdLayoutRoute,
  path: `/${routeSegments.requirementSummary}`,
  staticData: { pageName: "Generating requirement summary" },
  component: function RequirementSummaryLoadingRouteComp() {
    const { id } = requirementSummaryLoadingRoute.useParams();
    return <BidReportLoadingPage bidId={id} type={routeSegments.requirementSummary} />;
  },
});

export const qualificationReportLoadingRoute = createRoute({
  getParentRoute: () => bidIdLayoutRoute,
  path: `/${routeSegments.qualificationReport}`,
  staticData: { pageName: "Generating qualification report" },
  component: function QualificationReportLoadingRouteComp() {
    const { id } = qualificationReportLoadingRoute.useParams();
    return <BidReportLoadingPage bidId={id} type={routeSegments.qualificationReport} />;
  },
});

// Standalone /bid_responses routes (not under /bids)
function BidResponsesLayout() {
  return (
    <BidResponsesProcessingProvider>
      <Outlet />
    </BidResponsesProcessingProvider>
  );
}

export const bidResponsesLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/bid_responses",
  staticData: { pageName: "" },
  component: BidResponsesLayout,
});

export const bidResponsesIndexRoute = createRoute({
  getParentRoute: () => bidResponsesLayoutRoute,
  path: "/",
  staticData: { pageName: "Bid responses" },
  component: function BidResponsesIndexRouteComp() {
    return <BidResponsesListPage />;
  },
});

export const bidResponseDetailRoute = createRoute({
  getParentRoute: () => bidResponsesLayoutRoute,
  path: "/$id",
  staticData: { pageName: "Bid response" },
  component: function BidResponseDetailRouteComp() {
    const { id } = bidResponseDetailRoute.useParams();
    return <BidResponsePage bidResponseId={id} />;
  },
});

// Public shared bid / no-bid report
export const sharedBidReportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/shared/bid_reports/$bidReportId",
  staticData: { pageName: "Bid / no-bid report", noShell: true },
  component: function SharedBidReportRouteComp() {
    const { bidReportId } = sharedBidReportRoute.useParams();
    return <SharedBidReportView bidReportId={bidReportId} />;
  },
});

// Public shared qualification report
export const sharedQualificationReportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/shared/qualification_report/$bidReportId",
  staticData: { pageName: "Qualification report" },
  component: function SharedQualificationReportRouteComp() {
    const { bidReportId } = sharedQualificationReportRoute.useParams();
    return <PublicQualificationReport bidReportId={bidReportId} />;
  },
});

export const bidsRouteTree = bidsLayoutRoute.addChildren([
  bidsIndexRoute,
  bidIdLayoutRoute.addChildren([
    bidDetailRoute,
    bidSelfServeRoute,
    bidQualificationRoute,
    bidQualificationLegacyRedirectRoute,
    bidSelfServeLegacyRedirectRoute,
    bidResponsesLayoutRoute_nested.addChildren([bidResponsesIndexRoute_nested, bidResponseRoute]),
    bidDocumentsRoute,
    bidBuyerActivityRoute,
    qualificationReportRoute,
    requirementSummaryLoadingRoute,
    qualificationReportLoadingRoute,
  ]),
]);

export const bidResponsesRouteTree = bidResponsesLayoutRoute.addChildren([
  bidResponsesIndexRoute,
  bidResponseDetailRoute,
]);
